import React, {useEffect, useMemo, useRef, useState} from 'react'
import {Link} from 'react-router-dom'
import {
    Button, SectionTitle, OfferCard, FeatureList, PromoBand,
    Banner, Countdown, PrizeWheel, GiftCard, SegmentedControl, Tag,
} from '@azoya-spa/ui'
import '@azoya-spa/ui/styles.css'
import {
    computedServiceLabel, gamePeriodLabel, isLive, monthlyEquivalent,
    passComparison, passPriceUnit, preselectedPass, promotionLines,
} from '@azoya-spa/contracts'
import {useOffers} from '../../hooks/useOffers'
import {useCatalog} from '../../hooks/useCatalog'
import {useSiteContent} from '../../hooks/useSiteContent'
import practicalInfoFallback from '../../api/fallback/practicalInfo.json'
import {frenchNumberToTelHref} from '../../utils/phone'
import {PromotionTable} from './PromotionTable'
import BandeauDeConfiance from '../../components/trust/BandeauDeConfiance'
import {bookingCtaClicked, giftCardOpened, phoneClicked} from '../../analytics/events'
import "./OffresAbonnement.css"

// Tous les contenus de cette page viennent du back-office (lot 3). Ce qui était
// écrit en dur ici avant — prix des pass, avantages, lots de la roue, textes du
// hero, dates de la promotion — vit maintenant en base et se modifie depuis
// l'administration, sans développeur ni déploiement. Les coordonnées du panneau
// « Comment obtenir votre pass » suivent la même règle : elles sortent des
// informations pratiques, comme le pied de page.
//
// Rien n'est calculé deux fois : l'équivalent mensuel, la comparaison entre
// pass, l'étiquette de période du jeu et les prix remisés sortent tous de
// `@azoya-spa/contracts`, partagés avec l'administration qui les affiche en
// aperçu. C'est ce qui garantit que la gérante voit avant d'enregistrer
// exactement ce que la visiteuse verra.

/** Cibles possibles du lien du bandeau. Jamais une adresse libre côté saisie :
 * la correspondance vers une ancre réelle de la page est faite ici. */
const ANCRES = {
    promotion: '#promotion',
    pass: '#pass',
    jeu: '#jeu',
    'cartes-cadeaux': '#cartes-cadeaux',
}

function useScrollReveal(offers, catalog) {
    const rootRef = useRef(null)

    useEffect(() => {
        const root = rootRef.current
        if (!root) return

        const targets = root.querySelectorAll('[data-reveal]:not(.is-visible)')
        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    entry.target.classList.add('is-visible')
                    observer.unobserve(entry.target)
                }
            })
        // Seuil 0, pas un pourcentage du bloc : un seuil de 15 % est hors
        // d'atteinte pour un bloc plus de six fois plus haut que l'écran — une
        // promotion de 19 prestations fait 6 000 px sur un téléphone. Le bloc
        // restait alors invisible en occupant sa place : un long défilement
        // vide. La marge de 40 px garde l'apparition un peu après l'entrée.
        }, {threshold: 0, rootMargin: '0px 0px -40px 0px'})

        targets.forEach((el) => observer.observe(el))
        return () => observer.disconnect()
        // Les offres et le catalogue changent quand l'API remplace la copie de
        // secours : les blocs saisonniers qui apparaissent alors n'existaient
        // pas au premier passage de l'observateur et resteraient invisibles
        // sans cette seconde observation. Le catalogue compte autant que les
        // offres : le tableau d'une promotion n'existe que si ses prestations
        // y figurent, et une prestation récente est absente de la copie de
        // secours — le bloc n'apparaît alors qu'à l'arrivée du catalogue.
    }, [offers, catalog])

    return rootRef
}

export default function OffresAbonnement() {
    const {offers} = useOffers()
    const {catalog} = useCatalog()
    const infosPratiques = useSiteContent('practical-info', practicalInfoFallback)
    const [announcementOpen, setAnnouncementOpen] = useState(true)
    const revealRoot = useScrollReveal(offers, catalog)


    // ── Ce qui s'affiche aujourd'hui : armé ET dans la période ──
    const promotion = useMemo(
        () => offers.promotions.find((p) => isLive(p.season)) ?? null,
        [offers.promotions],
    )
    const jeu = useMemo(() => offers.games.find((g) => isLive(g.season)) ?? null, [offers.games])
    const bandeauVisible = isLive(offers.banner.season)

    const passVisibles = useMemo(() => offers.passes.filter((pass) => pass.visible), [offers.passes])

    // Le sélecteur de durée se déduit des pass visibles : rien à saisir pour lui.
    const optionsDuree = useMemo(
        () => passVisibles.map((pass) => ({
            value: pass.id,
            label: pass.durationMonths === 12 ? '12 mois' : `${pass.durationMonths} mois`,
        })),
        [passVisibles],
    )

    // Le pass présélectionné ouvre la page en avant ; la visiteuse peut ensuite
    // changer. `null` tant que les pass ne sont pas connus, pour ne pas figer un
    // choix fait sur la copie de secours si l'API en renvoie d'autres.
    const [passChoisi, setPassChoisi] = useState(null)
    const passMisEnAvant = passChoisi ?? preselectedPass(offers.passes)?.id ?? null

    // Prestations du catalogue indexées par identifiant, pour le tableau de la
    // promotion. Les prix viennent de là et de nulle part ailleurs.
    const prestationsParId = useMemo(() => {
        const index = new Map()
        for (const famille of catalog) {
            for (const groupe of famille.groups) {
                for (const service of groupe.services) {
                    index.set(service.id, {
                        label: computedServiceLabel(service),
                        name: service.name,
                        group: groupe.name,
                        amounts: service.amounts,
                    })
                }
            }
        }
        return index
    }, [catalog])

    const lignesPromo = useMemo(
        () => (promotion ? promotionLines(promotion, prestationsParId) : []),
        [promotion, prestationsParId],
    )

    // Le compte à rebours vise la fin de période de la promotion : une seule
    // date, jamais saisie deux fois. Fin de journée, pour que le dernier jour
    // compte entièrement.
    const finPromotion = useMemo(
        () => (promotion ? new Date(`${promotion.season.end}T23:59:59`) : null),
        [promotion],
    )

    const ancre = ANCRES[offers.banner.target]

    const promotionVisible = promotion !== null && lignesPromo.length > 0

    // L'index ne dit jamais qu'il manque quelque chose : il compte simplement
    // une entrée de moins. C'est le seul élément de la page qui « sait » que la
    // saison est finie, et il n'en fait pas état.
    const index = [
        passVisibles.length > 0 && {label: 'Comparer les pass', href: '#pass'},
        promotionVisible && {label: 'La promotion du moment', href: '#promotion'},
        {label: 'Offrir une carte cadeau', href: '#cartes-cadeaux'},
    ].filter(Boolean)

    return (
        <div className='OffresAbonnement' ref={revealRoot}>

            {/* 1. Bandeau d'annonce — armé et dans sa période. */}
            {bandeauVisible && announcementOpen && (
                <div className="oa-banner">
                    <Banner tone="promo" icon="✦" onClose={() => setAnnouncementOpen(false)}>
                        {offers.banner.text}
                        {ancre && offers.banner.linkLabel.trim() && (
                            <>{' '}<a href={ancre}>{offers.banner.linkLabel}</a></>
                        )}
                    </Banner>
                </div>
            )}

            {/* 2. Titre. Pas d'image : le sujet de cette page est un prix et une
                 durée d'engagement, une photo d'ambiance ne ferait que retarder
                 les deux cartes. L'atmosphère est portée par l'accueil. */}
            <div className="oa-hero">
                <h1 className="oa-hero-title">{offers.texts.heroTitle}</h1>
                <p className="oa-hero-text">{offers.texts.heroIntro}</p>
            </div>

            {/* 3. Index — les trois intentions d'arrivée servies avant tout
                 défilement. Il remplace le fil d'Ariane maison, qui à un seul
                 niveau n'apprenait rien : c'est l'entrée « Offres/Abonnement »
                 du Header qui dit désormais où l'on est. */}
            {index.length > 1 && (
                <nav className="oa-index" aria-label="Sur cette page">
                    {index.map((entree) => (
                        <a className="oa-index-link" href={entree.href} key={entree.href}>
                            {entree.label}
                            <span aria-hidden="true">↓</span>
                        </a>
                    ))}
                </nav>
            )}

            {/* 4. Pass abonnements, juste après le titre : c'est le produit
                 d'engagement, et c'est ce qui fait tenir la page hors saison. */}
            {passVisibles.length > 0 && (
                <div className="oa-reveal oa-section" data-reveal id="pass">
                    <div className="oa-passes-header">
                        <div>
                            <SectionTitle>Les Pass Abonnements</SectionTitle>
                            <p className="oa-section-intro">Un engagement, des avantages durables. Choisissez la formule qui accompagne votre rythme.</p>
                        </div>
                        {optionsDuree.length > 1 && (
                            <SegmentedControl
                                label="Durée d’engagement"
                                options={optionsDuree}
                                value={passMisEnAvant ?? optionsDuree[0].value}
                                onChange={setPassChoisi}
                            />
                        )}
                    </div>

                    <div className={`oa-passes-grid${passVisibles.length === 1 ? ' oa-passes-grid--seule' : ''}`}>
                        {passVisibles.map((pass) => {
                            const comparaison = passComparison(pass, offers.passes)
                            /* Aucun `booking_cta_clicked` sur le bouton de cette
                               carte : il ne mène plus à la réservation mais à
                               l'ancre « souscrire », plus bas sur cette même page.
                               Le seul chemin de la page Offres vers
                               /reserver-en-ligne est dans ce panneau-là, et c'est
                               lui qui porte l'emplacement `offres_souscrire`. */
                            return (
                                <OfferCard
                                    key={pass.id}
                                    label="Abonnement"
                                    title={pass.title}
                                    price={String(Math.round(pass.priceCents / 100))}
                                    priceUnit={`€ ${passPriceUnit(pass)}`}
                                    featured={pass.id === passMisEnAvant}
                                    badge={pass.badge || undefined}
                                    cta={<Button as="a" href="#souscrire" variant="light">Comment obtenir ce pass</Button>}
                                >
                                    <div className="oa-pass-monthly">{monthlyEquivalent(pass)}</div>
                                    {comparaison && <div className="oa-pass-savings">{comparaison}</div>}
                                    <FeatureList items={pass.benefits}/>
                                </OfferCard>
                            )
                        })}
                    </div>

                    {/* Le bouton des cartes ne ment plus : il descend ici plutôt
                        que d'envoyer sur un module de prise de rendez-vous qui
                        ne connaît pas les pass. Ce panneau est là toute l'année,
                        et il écarte les deux surfaces sombres voisines quand la
                        saison est finie. */}
                    <div className="oa-souscrire" id="souscrire">
                        <h3 className="oa-souscrire-title">Comment obtenir votre pass</h3>
                        <p className="oa-souscrire-intro">
                            La souscription en ligne n’est pas encore possible : le pass se prend à
                            l’institut, et il est actif immédiatement.
                        </p>

                        <div className="oa-souscrire-grid">
                            <div className="oa-souscrire-voie">
                                <div className="oa-souscrire-voie-titre">À l’accueil</div>
                                <p>Lors de votre prochaine visite, {infosPratiques.address}.</p>
                            </div>
                            <div className="oa-souscrire-voie">
                                <div className="oa-souscrire-voie-titre">Par téléphone</div>
                                <p>
                                    {/* La souscription en ligne n'existe pas : ce numéro est la
                                        voie la plus directe vers un pass, et donc un chemin de
                                        conversion au moins aussi parlant que le lien de
                                        réservation qui ferme ce panneau. */}
                                    <a className="oa-souscrire-tel" href={frenchNumberToTelHref(infosPratiques.phone)}
                                       onClick={() => phoneClicked()}>
                                        {infosPratiques.phone}
                                    </a>
                                    {infosPratiques.openingLines?.length > 0 && (
                                        <><br/>{infosPratiques.openingLines.join(' · ')}</>
                                    )}
                                </p>
                            </div>
                            <div className="oa-souscrire-voie">
                                <div className="oa-souscrire-voie-titre">À la fin d’un soin</div>
                                <p>Votre praticienne l’active et le déduit du soin en cours.</p>
                            </div>
                        </div>

                        <p className="oa-souscrire-reserver">
                            Pas encore de rendez-vous ?{' '}
                            <Link to="/reserver-en-ligne"
                                  onClick={() => bookingCtaClicked('offres_souscrire')}>Réserver un soin →</Link>
                        </p>
                    </div>
                </div>
            )}

            {/* 5. Promotion — le compte à rebours n'est plus un bloc à part : il
                 est dans l'en-tête du tableau. Les deux ne formaient qu'un sujet
                 et faisaient deux objets. Le bloc disparaît proprement quand
                 aucune promotion n'est en ligne, ou quand celle qui l'est ne
                 porte encore aucune prestation. */}
            {promotionVisible && (
                <div className="oa-reveal oa-section" data-reveal id="promotion">
                    <div className="oa-promo">
                        <div className="oa-promo-band">
                            <div className="oa-promo-band-text">
                                <span className="oa-promo-dot" aria-hidden="true"/>
                                <div>
                                    <div className="oa-promo-eyebrow">{promotion.title}</div>
                                    {promotion.countdownTagline && (
                                        <h2 className="oa-promo-tagline">{promotion.countdownTagline}</h2>
                                    )}
                                </div>
                            </div>
                            {promotion.showCountdown && finPromotion && (
                                <div className="oa-promo-countdown" aria-label="Fin de la promotion">
                                    <Countdown endDate={finPromotion}/>
                                </div>
                            )}
                        </div>

                        <div className="oa-promo-body">
                            {promotion.intro && <p className="oa-section-intro">{promotion.intro}</p>}
                            <PromotionTable lignes={lignesPromo} promotion={promotion}/>
                        </div>
                    </div>
                </div>
            )}

            {/* 6. Cartes cadeaux. Le visuel recto/verso reste illustratif :
                 ses montants ne pilotent aucune vraie carte. */}
            <div className="oa-reveal oa-section" data-reveal id="cartes-cadeaux">
                <div className="oa-cadeaux">
                    <div>
                        <SectionTitle>{offers.texts.giftTitle}</SectionTitle>
                        <p className="oa-section-intro">{offers.texts.giftText}</p>
                        <Button as={Link} to="/offrir" onClick={() => giftCardOpened('offres_bouton')}>Offrir une carte cadeau</Button>
                    </div>

                    <Link to='/offrir' className="oa-giftcard-visual" aria-label="Offrir une carte cadeau"
                          onClick={() => giftCardOpened('offres_visuel')}>
                        <div className="oa-giftcard-row">
                            <GiftCard face="front" amount="120€" tagline="Auxerre · depuis 2014"/>
                            <GiftCard face="back" validUntil="07 / 2027" serial="N° AZ-2026-000903"/>
                        </div>
                    </Link>
                </div>
            </div>

            {/* 7. Jeu — après les cartes cadeaux : entre les deux, c'est la
                 carte cadeau qui s'achète. La roue ne se joue pas en ligne,
                 elle quitte donc le fond sombre, réservé à ce qui engage et à
                 ce qui expire. */}
            {jeu && (
                <div className="oa-reveal oa-section" data-reveal id="jeu">
                    <div className="oa-roue">
                        <div className="oa-roue-visuel">
                            <PrizeWheel prizes={jeu.config.prizes} size={220}/>
                            {jeu.config.caption && (
                                <div className="oa-roue-caption">{jeu.config.caption}</div>
                            )}
                        </div>

                        <div className="oa-roue-content">
                            <Tag variant="coral">{gamePeriodLabel(jeu)}</Tag>
                            <h2 className="oa-roue-title">{jeu.title}</h2>
                            {jeu.text && <p className="oa-roue-text">{jeu.text}</p>}

                            {jeu.config.steps.length > 0 && (
                                <ol className="oa-roue-steps">
                                    {jeu.config.steps.map((etape) => (
                                        <li key={etape}>{etape}</li>
                                    ))}
                                </ol>
                            )}

                            {/* Les lots sont sur la roue, mais y tournent à
                                11 px : ces jetons sont ce qui les rend
                                réellement lisibles. */}
                            <div className="oa-roue-lots">
                                {jeu.config.prizes.map((lot) => (
                                    <span className="oa-roue-lot" key={lot}>{lot}</span>
                                ))}
                            </div>

                            {jeu.legalNotice && <p className="oa-roue-fineprint">{jeu.legalNotice}</p>}
                        </div>
                    </div>
                </div>
            )}

            {/* 8. Arguments de confiance. Le même bandeau, au mot près, que
                 celui de la page d'accueil : un seul contenu au back-office,
                 un seul composant ici. */}
            <BandeauDeConfiance className="oa-reveal oa-section"/>

            {/* 9. Bande de clôture. */}
            {offers.texts.closingText && (
                <div className="oa-reveal oa-section" data-reveal>
                    <PromoBand tone="dark">{offers.texts.closingText}</PromoBand>
                </div>
            )}

        </div>
    )
}
